import {
  Avatar,
  Link,
  LinkBox,
  LinkOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import * as React from 'react';

import { SearchHelpers } from '@/client/legacy-shared-modules/search/helpers';
import {
  EPostSearchType,
  IPostSearch,
  IPostSearch_Comment,
  ViewableAttributesField,
} from '@/client/legacy-shared-modules/search/search-types';
import { CustomHighlight } from '@/components/CustomHighlight';
import { CustomSnippet } from '@/components/CustomSnippet';
import { FormattedUserName } from '@/components/FormattedUserName';
import { formatDisplayName } from '@/utilities';
import { search_result_clicked } from '@/analytics';

const ACTION = {
  [EPostSearchType.BLOG]: 'posted an article',
  [EPostSearchType.POLL]: 'posted a poll',
  [EPostSearchType.POST]: 'posted',
  [EPostSearchType.COMMENT]: 'replied',
  [EPostSearchType.QUESTION]: 'asked a question',
  [EPostSearchType.ANSWER]: 'posted an answer',
};

const TITLE_ATTRIBUTE = {
  [EPostSearchType.BLOG]: 'blog.title',
  [EPostSearchType.POLL]: 'poll.title',
  [EPostSearchType.POST]: 'title',
  [EPostSearchType.QUESTION]: 'question.title',
};

const getCommentTitle = (hit: IPostSearch_Comment): string => {
  if (!hit.post || typeof hit.post !== 'object' || !('type' in hit.post)) {
    return (
      (hit as unknown as { title?: string }).title ||
      hit.bodyPlainText ||
      'Comment'
    );
  }

  switch (hit.post.type) {
    case EPostSearchType.BLOG:
      return hit.post.blog.title;
    case EPostSearchType.POLL:
      return hit.post.poll.title;
    case EPostSearchType.POST:
      return hit.post.title;
    case EPostSearchType.ANSWER:
      return hit.post.answer.body;
    case EPostSearchType.QUESTION:
      return hit.post.question.title;
    default:
      return (
        (hit as unknown as { title?: string }).title ||
        hit.bodyPlainText ||
        'Comment'
      );
  }
};

const BODY_ATTRIBUTE = {
  [EPostSearchType.BLOG]: 'blog.content',
  [EPostSearchType.POLL]: 'poll.description',
  [EPostSearchType.POST]: 'bodyPlainText',
  [EPostSearchType.COMMENT]: 'bodyPlainText',
  [EPostSearchType.QUESTION]: 'question.body',
  [EPostSearchType.ANSWER]: 'answer.body',
};

const getAnswerQuestionTitle = (hit: IPostSearch): string =>
  (hit as unknown as { answer?: { question?: { title?: string } } }).answer
    ?.question?.title ||
  (hit as unknown as { title?: string }).title ||
  '';

export const Post: React.FC<{
  hit: IPostSearch;
  query?: string;
  position?: number;
}> = ({ hit, query, position }) => {
  const viewableAttributesField: ViewableAttributesField =
    (hit.adminGlobalViewable && 'adminGlobalViewable') ||
    (hit.membersWithMemberStatusGlobalViewable &&
      'membersWithMemberStatusGlobalViewable') ||
    (hit.loggedInGlobalViewable && 'loggedInGlobalViewable') ||
    'loggedInGlobalViewable'; // Provide default to avoid undefined
  const authorAttributes = hit[viewableAttributesField];

  // Format the display name using the utility function
  const displayName = React.useMemo(() => {
    if (!authorAttributes) {
      return hit.displayName || '';
    }

    return formatDisplayName({
      preferredName: authorAttributes.preferredName,
      prefix: authorAttributes.prefix,
      firstName: authorAttributes.firstName,
      lastName: authorAttributes.lastName,
      suffix: authorAttributes.suffix,
      nameTitle: authorAttributes.nameTitle,
      // jobTitle is not available in post search authorAttributes
    });
  }, [authorAttributes, hit.displayName]);

  const handleClick = () => {
    // Track search result click with meaningful context
    search_result_clicked({
      entity: {
        id: hit.objectID,
        type: 'post',
      },
      search: {
        query: query || '',
        searchType: 'posts',
      },
      engagement: {
        position,
      },
    });
  };

  return (
    <LinkBox
      as={Stack}
      spacing="1"
      p="2"
      bg="white"
      _hover={{ bg: 'gray.100' }}
      borderRadius="xl"
      cursor="pointer"
      onClick={handleClick}
    >
      <Stack direction="row" alignItems="center">
        <Avatar
          size="xs"
          src={`${viewableAttributesField}.profilePictureUrl` || undefined}
          name={displayName}
        />

        <Text textStyle="compact" color="gray.600">
          <Link as={NextLink} href={`/members/${hit.authorId}`}>
            <FormattedUserName
              hit={hit}
              viewableAttributesField={viewableAttributesField}
              fallbackDisplayName={hit.displayName}
            />
          </Link>{' '}
          {ACTION[hit.postType]} in{' '}
          {hit.group && (
            <Link
              as={NextLink}
              href={`/community/groups/${hit.group._id}/activity`}
            >
              {hit.group.name}
            </Link>
          )}
          {hit.organization && (
            <Link as={NextLink} href={`/companies/${hit.organization._id}`}>
              {hit.organization.name}
            </Link>
          )}
        </Text>
      </Stack>

      <Stack spacing="0">
        <LinkOverlay as={NextLink} href={SearchHelpers.getPostUrl(hit)}>
          <Text noOfLines={1} textStyle="bodyStrong">
            {hit.postType === EPostSearchType.COMMENT ? (
              getCommentTitle(hit)
            ) : hit.postType === EPostSearchType.ANSWER ? (
              getAnswerQuestionTitle(hit)
            ) : (
              <CustomHighlight
                attribute={TITLE_ATTRIBUTE[hit.postType]}
                hit={hit}
              />
            )}
          </Text>
        </LinkOverlay>

        <Text noOfLines={2} textStyle="compact" color="gray.600">
          <CustomSnippet attribute={BODY_ATTRIBUTE[hit.postType]} hit={hit} />
        </Text>
      </Stack>
    </LinkBox>
  );
};
